import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EmailClient } from '@app-services/api/clients/email-client';
import { EmailTagClient } from '@app-services/api/clients/email-tag.client';
import { FileClient } from '@app-services/api/clients/file.client';
import { MailFolderClient } from '@app-services/api/clients/mail-folder.client';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';
import {
  BaseGetByEmailFileIdRequest,
  BaseGetByFileIdRequest,
  BaseGetByIdRequest,
  BaseGetCollectionByIdRequest,
  BasePostCommentRequest,
} from '@app-types/base/base';
import { EmailContract, EmailListItemContract } from '@app-types/api/email';
import { EmailValidationState } from '@app-types/enums/email.validation-state';
import { PermissionService } from '@app-services/permission/permission.service';
import { MailFolderType } from '@app-types/enums/mail-folder.type';
import { EmailAuditClient } from '@app-services/api/clients/email-audit-client';
import { EmailAuditContract } from '@app-types/api/emails/email-audit';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { PermissionType } from '@app-types/enums/permission-type';
import { MailFolderSelectDialogComponent } from '../../mail-account-folders/mail-folder-select-dialog/mail-folder-select-dialog.component';
import { EmailState } from 'src/app/types/enums/email-state';
import { SomethingWentWrongComponent } from '../../common/error/something-went-wrong/something-went-wrong.component';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EmailMenuContent } from '@app-types/enums/emails';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EmailCreateOrUpdateComponent } from '../email-create-or-update/email-create-or-update.component';
import { EmailBodyComponent } from '../../common/email/email-body/email-body.component';
import { EmailSidenavComponent } from '../../common/email/email-sidenav/email-sidenav.component';
import { MatMenuModule } from '@angular/material/menu';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { DrawerService } from '@app-services/drawer.service';
import { LoadingButtonComponent } from '@app-components/common/loading-button/loading-button.component';
import { SkeletonEmailComponent } from '@app-components/common/skeletons/skeleton-email/skeleton-email.component';
import { Attachment } from '@app-types/api/attachment';
import {
  PreparedEmailBodyResult,
  excludeInlineAttachments,
  prepareEmailBodyHtml,
  revokeInlineImageUrls,
} from '@app-shared/utils/email-body';
import { ChangedTags, TagContract } from '@app-types/api/tag';
import { EmailAttachmentsListComponent } from '@app-components/email/email-attachments-list/email-attachments-list.component';
import { FormatSemicolonPipe } from '@app-pipes/format-semicolon-pipe';
import { ActionsService } from '@app-services/emails/button-actions/actions.service';
import { AuditButtonComponent } from '@app-components/email/action-buttons/audit-button/audit-button.component';
import { DeleteButtonComponent } from '@app-components/email/action-buttons/delete-button/delete-button.component';
import { DownloadSingleEmlButtonComponent } from '@app-components/email/action-buttons/download-single-eml-button/download-single-eml-button.component';
import { ReplyButtonComponent } from '@app-components/email/action-buttons/reply-button/reply-button.component';
import { ReplyAllButtonComponent } from '@app-components/email/action-buttons/reply-all-button/reply-all-button.component';
import { ForwardButtonComponent } from '@app-components/email/action-buttons/forward-button/forward-button.component';
import { ForwardAttachmentButtonComponent } from '@app-components/email/action-buttons/forward-attachment-button/forward-attachment-button.component';
import { ValidateButtonComponent } from '@app-components/email/action-buttons/validate-button/validate-button.component';
import { NotesButtonComponent } from '@app-components/email/action-buttons/notes-button/notes-button.component';
import { DownloadEmlButtonComponent } from '@app-components/email/action-buttons/download-eml-button/download-eml-button.component';
import { MarkAsSpanButtonComponent } from '@app-components/email/action-buttons/mark-as-span-button/mark-as-span-button.component';
import { MarkAsNotSpanButtonComponent } from '@app-components/email/action-buttons/mark-as-not-span-button/mark-as-not-span-button.component';
import { SetUnreadButtonComponent } from '@app-components/email/action-buttons/set-unread-button/set-unread-button.component';
import { SetReadButtonComponent } from '@app-components/email/action-buttons/set-read-button/set-read-button.component';
import { RestoreButtonComponent } from '@app-components/email/action-buttons/restore-button/restore-button.component';
import { FieldTagsListComponent } from '@app-components/common/field-tags-list/field-tags-list.component';

@Component({
  selector: 'app-email-detail-dialog',
  templateUrl: './email-detail-dialog.component.html',
  styleUrls: ['./email-detail-dialog.component.scss'],
  imports: [
    SomethingWentWrongComponent,
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    DatePipe,
    MatProgressSpinnerModule,
    CKEditorModule,
    MatTooltipModule,
    EmailCreateOrUpdateComponent,
    EmailBodyComponent,
    EmailSidenavComponent,
    MatMenuModule,
    LoadingButtonComponent,
    SkeletonEmailComponent,
    EmailAttachmentsListComponent,
    FormatSemicolonPipe,
    AuditButtonComponent,
    DeleteButtonComponent,
    DownloadSingleEmlButtonComponent,
    ReplyButtonComponent,
    ReplyAllButtonComponent,
    ForwardButtonComponent,
    ForwardAttachmentButtonComponent,
    ValidateButtonComponent,
    NotesButtonComponent,
    DownloadEmlButtonComponent,
    MarkAsSpanButtonComponent,
    MarkAsNotSpanButtonComponent,
    SetUnreadButtonComponent,
    SetReadButtonComponent,
    RestoreButtonComponent,
    FieldTagsListComponent,
  ],
})
export class EmailDetailDialogComponent implements OnInit, OnDestroy {
  @ViewChild('tags') tagsElement: ElementRef;
  @Input() public emailId: number;
  @Input() public mailFolderId: number;
  @Input() public useMobileView = false;
  @Input() currentFolderType: number;
  @Input() notesCount: number;
  @Output() public closeDrawer = new EventEmitter();
  @Output() public getInfoAboutOpenedEmail = new EventEmitter<any>();
  @Output() public setSeenOpened = new EventEmitter<any>();
  @Output() public deleteFromData = new EventEmitter<any>();
  @Output() public refreshNotes = new EventEmitter();

  public isLoading = false;
  public cannotLoadEmailDetails = false;
  public emailStates = EmailState;
  public emailDetails: EmailContract;
  public emailValidationState = EmailValidationState;
  public mailAccountId: number;
  public canValidate = false;
  public thisTags: TagContract[] = [];
  public menuIsOpened = false;
  public menuContent: EmailMenuContent | null;
  public createOrUpdateDrawerIsOpen = false;
  public updatedEmailId: number | null;
  public menuTitle: string | null;
  public body: string;
  public visibleAttachmentFiles: Attachment[] = [];
  public approveInProgress = false;
  public rejectInProgress = false;
  public isDownloadingEML = false;
  public isDownloadingSingleEML = false;
  public validatingEmail = false;
  public isAuditLoading = false;
  public isAuditLoadingError = false;
  public isCurrentElementTags = false;
  public isReplying = false;
  public isReplyingAll = false;
  public isForwarding = false;
  public isForwardingAttachment = false;

  private inlineImageObjectUrls: string[] = [];

  public emailDrawerIsOpen = false;
  public openedEmail: EmailListItemContract | null;
  public events: EmailAuditContract[] | null;
  public displayedColumns: string[] = [
    'eventType',
    'userName',
    'eventDateTime',
    'metadata',
  ];
  public mailFolderType = MailFolderType;

  typeOne = [
    this.mailFolderType.Inbox,
    this.mailFolderType.Sent,
    this.mailFolderType.Validation,
    this.mailFolderType.Spam,
    this.mailFolderType.Archive,
  ];

  typeTwo = [
    this.mailFolderType.Sent,
    this.mailFolderType.Archive,
    this.mailFolderType.Outbox,
    this.mailFolderType.Trash,
  ];

  typeThree = [
    this.mailFolderType.Inbox,
    this.mailFolderType.Sent,
    this.mailFolderType.Draft,
    this.mailFolderType.Validation,
    this.mailFolderType.Spam,
    this.mailFolderType.Archive,
  ];

  typeFour = [
    this.mailFolderType.Inbox,
    this.mailFolderType.Sent,
    this.mailFolderType.Validation,
    this.mailFolderType.Spam,
    this.mailFolderType.Trash,
    this.mailFolderType.Archive,
  ];

  constructor(
    protected router: Router,
    private emailClient: EmailClient,
    private fileClient: FileClient,
    private tempFileClient: TempFileClient,
    public dialog: MatDialog,
    public matchError: MatchError,
    private permissionService: PermissionService,
    private emailTagClient: EmailTagClient,
    private emailAuditClient: EmailAuditClient,
    private mailFolderClient: MailFolderClient,
    private userWorkspaceService: UserWorkspaceService,
    private drawerService: DrawerService,
    private actionsService: ActionsService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.getEmailDetail(true);
    this.canValidate = await this.permissionService.hasPermissionOver(
      null,
      this.mailAccountId,
      PermissionType.CanValidateEmail
    );
  }

  async onDestroy(): Promise<void> {
    this.closeEmailModalForm();
  }

  ngOnDestroy(): void {
    revokeInlineImageUrls(this.inlineImageObjectUrls);
    this.onDestroy();
  }

  async setValidated(): Promise<void> {
    this.validatingEmail = true;
    try {
      await this.emailClient.setValidated({
        emailsId: [this.emailId],
        validated: EmailValidationState.Validated,
      });
      this.actionsService.updateStateAfterValidated([this.emailId]);
      this.emailDetails.validationState = EmailValidationState.Validated;
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.validatingEmail = false;
    }
  }

  // open new detail dialog
  public openEmailDetails(email: EmailListItemContract): void {
    this.openedEmail = email;
    this.emailDrawerIsOpen = true;
  }

  public checkingTheActiveElement(): void {
    const activeElement = document.activeElement;
    this.isCurrentElementTags =
      !!this.tagsElement?.nativeElement?.contains(activeElement);
  }

  public closeEmailModalForm(): void {
    this.closeDrawer.emit();
  }

  async getEmailDetail(isInit: boolean = false): Promise<void> {
    this.isLoading = true;
    this.cannotLoadEmailDetails = false;
    try {
      const request = new BaseGetByIdRequest(this.emailId);
      const result = await this.emailClient.getById(request);
      this.emailDetails = result.email;
      revokeInlineImageUrls(this.inlineImageObjectUrls);
      this.inlineImageObjectUrls = [];

      if (this.emailDetails.body) {
        const prepared = await this.prepareBody(
          this.emailDetails.body,
          this.emailDetails.attachmentFiles ?? []
        );
        this.body = prepared.body;
        this.inlineImageObjectUrls = prepared.objectUrls;
        this.visibleAttachmentFiles = excludeInlineAttachments(
          this.emailDetails.attachmentFiles ?? [],
          prepared.inlineAttachmentFileIds
        );
      } else {
        this.body = '';
        this.visibleAttachmentFiles = this.emailDetails.attachmentFiles ?? [];
      }
      this.getInfoAboutOpenedEmail.emit(this.emailDetails);
      const folderResponse = await this.mailFolderClient.get(
        new BaseGetByIdRequest(this.emailDetails.mailFolderId ?? null)
      );
      this.mailAccountId = folderResponse.result.mailAccountId;
      this.currentFolderType = folderResponse.result.folderType;

      const tagsResponse = await this.emailTagClient.getEmailTags(
        new BaseGetByIdRequest(this.emailId)
      );

      this.thisTags = tagsResponse.data;
      this.emailDetails.tags = tagsResponse.data;
      if (isInit && !this.emailDetails.seen) {
        this.setSeen(true, true);
      }
    } catch (e) {
      this.cannotLoadEmailDetails = true;
      this.matchError.logError(e);
    } finally {
      this.isLoading = false;
    }
  }

  get haveAttachments(): boolean {
    return this.visibleAttachmentFiles.length > 0;
  }

  async reply(): Promise<any> {
    try {
      this.isReplying = true;
      const newEmail = await this.emailClient.reply({ emailId: this.emailId });
      this.openCreateOrUpdateModalForm(newEmail.id, '');
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isReplying = false;
    }
  }

  async replyAll(): Promise<any> {
    try {
      this.isReplyingAll = true;
      const newEmail = await this.emailClient.replyAll({
        emailId: this.emailId,
      });
      this.openCreateOrUpdateModalForm(newEmail.id, '');
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isReplyingAll = false;
    }
  }

  async forward(): Promise<any> {
    try {
      this.isForwarding = true;
      const newEmail = await this.emailClient.forward({
        emailId: this.emailId,
      });
      this.openCreateOrUpdateModalForm(newEmail.id, '');
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isForwarding = false;
    }
  }

  async forwardAsAttachment(): Promise<any> {
    try {
      this.isForwardingAttachment = true;
      const newEmail = await this.emailClient.forwardMultiple({
        emailIds: [this.emailId],
        mailAccountId: this.mailAccountId,
      });
      this.openCreateOrUpdateModalForm(newEmail.id, '');
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isForwardingAttachment = false;
    }
  }

  async delete(): Promise<any> {
    try {
      await this.emailClient.delete({ emailIds: [this.emailId] });
      this.emailDetails.deleted = !this.emailDetails.deleted;
      this.drawerService.closeDrawer();
      this.closeDrawer.emit();
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  async restoreFromTrash(): Promise<any> {
    const dialogRef = this.dialog.open(MailFolderSelectDialogComponent, {
      width: '350px',
      autoFocus: false,
      data: {
        mailAccountId: this.mailAccountId,
        ids: this.emailId,
        isRestore: true,
        folderType: this.currentFolderType,
        isTrash: true,
        sourceFoldersType: this.emailDetails.sourceFolderType,
        title: 'Select the folder to restore',
      },
    });

    dialogRef
      .afterClosed()
      .subscribe(async (e: { confirmed: boolean; folderId: number }) => {
        if (e?.confirmed) {
          try {
            await this.emailClient.restore({
              emailIds: [this.emailId],
              mailFolderId: e.folderId,
            });
            this.emailDetails.deleted = !this.emailDetails.deleted;
            this.closeDrawer.emit();
          } catch (e) {
            this.matchError.errorHandler(e);
            this.matchError.logError(e);
          }
        }
      });
  }

  async restoreMoveFolderChoose(
    ids: number[],
    operationType: string
  ): Promise<any> {
    if (ids.length > 0) {
      const dialogRef = this.dialog.open(MailFolderSelectDialogComponent, {
        width: '350px',
        autoFocus: false,
        data: {
          mailAccountId: this.mailAccountId,
          ids,
          isRestore: true,
          folderType: this.currentFolderType,
          title: 'Select the folder to restore',
        },
      });
      dialogRef
        .afterClosed()
        .subscribe((e: { confirmed: boolean; folderId: number }) => {
          if (e?.confirmed) {
            if (operationType === 'restoreOneSpam') {
              this.setNotSpam(ids, e.folderId);
            }
          }
        });
    }
  }

  async setNotSpam(id: number[], folderId: number): Promise<any> {
    try {
      await this.emailClient.setNotSpam({
        emailIds: id,
        mailFolderId: folderId,
      });
      this.emailDetails.isSpam = !this.emailDetails.isSpam;
      this.closeDrawer.emit();
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  async isSpam(): Promise<any> {
    try {
      await this.emailClient.setIsSpam({ emailIds: [this.emailId] });
      this.emailDetails.isSpam = !this.emailDetails.isSpam;
      this.drawerService.closeDrawer();
      this.closeDrawer.emit();
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  async setSeen(b: boolean, isFirstOpen: boolean = false): Promise<any> {
    try {
      await this.emailClient.setSeen({ emailIds: [this.emailId], seen: b });
      this.setSeenOpened.emit({
        b,
        emailId: this.emailId,
        folderId: this.emailDetails.mailFolderId,
      });
      this.emailDetails.seen = !this.emailDetails.seen;
      if (isFirstOpen) {
        this.emailDetails.seen = true;
      }
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  public showIcon(iconTypes: MailFolderType[]): boolean {
    return iconTypes.includes(this.currentFolderType);
  }

  async approve(): Promise<any> {
    this.approveInProgress = true;
    try {
      await this.emailClient.validationApprove(
        new BaseGetByIdRequest(this.emailId)
      );
      this.approveInProgress = false;
      this.deleteFromData.emit(this.emailId);
      this.userWorkspaceService.refreshUnreadForFolder(
        this.mailFolderId,
        this.mailAccountId
      );
      this.drawerService.closeDrawer();
      this.closeDrawer.emit();
    } catch (e) {
      this.matchError.errorHandler(e);
      this.approveInProgress = false;
      this.matchError.logError(e);
    }
  }

  async reject(): Promise<any> {
    this.rejectInProgress = true;
    try {
      await this.emailClient.validationReject(
        new BasePostCommentRequest(this.emailId, '')
      );
      this.rejectInProgress = false;
      this.deleteFromData.emit(this.emailId);
      this.userWorkspaceService.refreshUnreadForFolder(
        this.mailFolderId,
        this.mailAccountId
      );
      this.drawerService.closeDrawer();
      this.closeDrawer.emit();
    } catch (e) {
      this.rejectInProgress = false;
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  print(): void {
    window.open(
      `mail-account/${this.mailAccountId}/mail-folder/${
        this.emailDetails.mailFolderId
      }/emails-content/${this.emailId}/print?id=${this.userWorkspaceService.currentOrganization?.organizationId ?? ''}`,
      '_blank'
    );
  }

  public showNotifications(): void {
    this.menuContent = EmailMenuContent.Notifications;
    this.menuIsOpened = true;
  }

  async audit(): Promise<void> {
    this.menuContent = EmailMenuContent.Audit;
    this.menuIsOpened = true;
    await this.getEvents();
  }

  async getEvents(): Promise<any> {
    this.isAuditLoadingError = false;
    this.isAuditLoading = true;
    try {
      const response = await this.emailAuditClient.getByEmail(
        new BaseGetCollectionByIdRequest(this.emailId, 1, 100)
      );
      this.events = response.data;
    } catch (e) {
      this.isAuditLoadingError = true;
      this.matchError.logError(e);
    } finally {
      this.isAuditLoading = false;
    }
  }

  public showNotes(): void {
    this.menuContent = EmailMenuContent.Notes;
    this.menuIsOpened = true;
  }

  public closeMenu(): void {
    this.menuIsOpened = false;
    this.menuContent = null;
  }

  public getDate(email: EmailContract): string {
    return this.currentFolderType === MailFolderType.Outbox ||
      this.currentFolderType === MailFolderType.Draft ||
      this.currentFolderType === MailFolderType.Validation
      ? email.stateDateTime
      : email.sentDateTime;
  }

  public openCreateOrUpdateModalForm(emailId: number, title: string): void {
    this.updatedEmailId = emailId;
    this.menuTitle = title;
    this.createOrUpdateDrawerIsOpen = true;
  }

  public async downloadSingleEML(): Promise<void> {
    this.isDownloadingSingleEML = true;
    try {
      const fileResponse = await this.emailClient.singleDownload({
        emailIds: [this.emailId],
      });
      const request = new BaseGetByEmailFileIdRequest(
        fileResponse.fileId,
        this.emailId
      );
      await this.fileClient.download(request);
    } catch (e) {
      this.matchError.errorHandler(e);
    } finally {
      this.isDownloadingSingleEML = false;
    }
  }

  public async downloadEML(): Promise<void> {
    this.isDownloadingEML = true;
    try {
      const fileResponse = await this.emailClient.download({
        emailIds: [this.emailId],
      });
      const request = new BaseGetByFileIdRequest(fileResponse.fileId);
      await this.tempFileClient.download(request);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isDownloadingEML = false;
    }
  }

  openAddTag(): void {
    this.menuContent = EmailMenuContent.Tags;
    this.menuIsOpened = true;
  }

  async createTag(newTag: ChangedTags): Promise<void> {
    this.thisTags = newTag.tags;
    this.emailDetails = {
      ...this.emailDetails,
      tags: newTag.tags,
    };
    this.menuContent = null;
    this.menuIsOpened = false;
  }

  private async prepareBody(
    rawBody: string,
    attachments: Attachment[]
  ): Promise<PreparedEmailBodyResult> {
    return prepareEmailBodyHtml(rawBody, attachments, async attachment => {
      if (!attachment.fileId) {
        return undefined;
      }

      const response = await this.fileClient.getContent(
        new BaseGetByEmailFileIdRequest(attachment.fileId, this.emailId)
      );

      if (!response?.file) {
        return undefined;
      }

      return {
        file: response.file,
        type: response.type || attachment.mimeType,
      };
    });
  }
}
